import React, { useCallback, useContext, useState } from 'react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getSurveyTokenFromId } from 'reducers'
import * as R from 'ramda'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'

import useAxios, { mutate } from 'hooks/useAxios'
import { toast } from 'libs/toast'
import OneoffEmailSendButton from '.'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { getDayjsToday, getDefaultHoursSchedule, getWorkspaceTimezone } from 'libs/date'

/**
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {boolean} [props.isSurveyClosed]
 */
export default function OneoffEmailSendButtonContainer({ className, isSurveyClosed }) {
  const surveyToken = useSurveyToken()
  const [scheduleDate, setScheduleDate] = useState(getDayjsToday())
  const defaultHoursSchedule = getDefaultHoursSchedule()

  const [scheduleTime, setScheduleTime] = useState(defaultHoursSchedule)
  const handleSetScheduleDate = useCallback(
    (selectedDate) => {
      setScheduleDate(selectedDate)
      if (isToday(selectedDate)) {
        setScheduleTime((scheduleTime) => {
          if (scheduleTime > defaultHoursSchedule) {
            return scheduleTime
          }

          return defaultHoursSchedule
        })
      }
    },
    [defaultHoursSchedule]
  )

  const { isSubmitting, isEmailActionButtonDisable } = useContext(surveySettingContext)
  const numberOfRecipients = useNumberOfRecipients()

  const workspaceTimezone = getWorkspaceTimezone()
  const { fetch, loading: sendImmediatelyEmailLoading } = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/send-oneoff-email-survey/`,
    data: {
      sending_type: 'immediately',
      time_zone: workspaceTimezone,
    },
    skip: true,
  })
  const { fetch: scheduleOneOff, loading: createScheduleLoading } = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/send-oneoff-email-survey/`,
    skip: true,
  })

  const { fetch: cancelScheduleOneOff } = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/cancel-scheduled-send-oneoff-email/`,
    skip: true,
  })

  const { data: emailSurveySetting } = useAxios({
    url: `survey/survey/${surveyToken}/email-survey-setting/`,
    skip: !surveyToken,
  })

  const history = useHistory()
  const match = useRouteMatch()
  const createOnSendImmediately = debounce((onSuccess) => {
    fetch()
      .then(() => {
        onSuccess()
        _updateSurveyLabelStatus('sent', surveyToken)
        _addSendNowHistory(numberOfRecipients, surveyToken)
        toast.success('Your email has been queued up for instant delivery.')
        history.push(`${match.url}/publish/delivery/`)
      })
      .catch((error) => {
        const errorMessage = R.prop('detail', error.response.data)
        if (errorMessage) {
          toast.error(errorMessage)
        } else if (error.response.status == 400) {
          toast.error(
            "Oops! Looks like your account is inactive. To send bulk emails, you'll need to choose a plan. Let's get you back on track!"
          )
        } else {
          toast.error('Failed to send emails. Please try again or contact Simplesat support.')
        }
      })
  }, 500)

  const createOnScheduleSend = debounce((onSuccess) => {
    const timestamp = scheduleDate.hour(scheduleTime).minute(0).toISOString()
    scheduleOneOff({
      data: {
        sending_type: 'scheduled',
        time_zone: workspaceTimezone,
        timestamp,
      },
    })
      .then(() => {
        onSuccess()
        _updateSurveyLabelStatus('scheduled', surveyToken)
        _addScheduledHistory(timestamp, surveyToken)
        toast.success('Scheduled successfully')
      })
      .catch((error) => {
        const errorMessage = R.prop('detail', error.response.data)
        if (errorMessage) {
          toast.error(errorMessage)
        } else if (error.response.status == 400) {
          toast.error(
            "Oops! Looks like your account is inactive. To send bulk emails, you'll need to choose a plan. Let's get you back on track!"
          )
        } else {
          toast.error('Failed to send emails. Please try again or contact Simplesat support.')
        }
      })
  }, 500)

  const cancelScheduled = debounce(() => {
    cancelScheduleOneOff()
      .then(() => {
        _updateSurveyLabelStatus('draft', surveyToken)
        _removeScheduledHistory(surveyToken)
        toast.success('Email canceled successfully.')
      })
      .catch(() => {
        toast.error('Failed to canceled emails. Please try again or contact Simplesat support.')
      })
  }, 500)

  const oneOffSurveyState = R.prop('delivery_status', emailSurveySetting)

  return (
    <OneoffEmailSendButton
      className={className}
      scheduleDate={scheduleDate}
      setScheduleDate={handleSetScheduleDate}
      setScheduleTime={setScheduleTime}
      scheduleTime={scheduleTime}
      createOnSendImmediately={createOnSendImmediately}
      loading={sendImmediatelyEmailLoading || createScheduleLoading}
      createOnScheduleSend={createOnScheduleSend}
      disabled={
        isEmailActionButtonDisable || isSubmitting || !(numberOfRecipients > 0) || isSurveyClosed
      }
      numberOfRecipients={numberOfRecipients}
      surveyState={oneOffSurveyState}
      onCancelScheduled={cancelScheduled}
    />
  )
}

function isToday(dayjsDate) {
  return dayjs().isSame(dayjsDate, 'day')
}

function useSurveyToken() {
  const { surveyId } = useParams()
  return useSelector((state) => getSurveyTokenFromId(state, surveyId))
}

function _updateSurveyLabelStatus(newStatus, surveyToken) {
  const emailSurveySettingUrl = `survey/survey/${surveyToken}/email-survey-setting/`
  mutate(emailSurveySettingUrl, (data) => ({ ...data, delivery_status: newStatus }), false)
}

function _addSendNowHistory(numberOfRecipients, surveyToken) {
  const url = `survey/survey/${surveyToken}/email-delivery-history/`
  const newHistory = {
    email_type: 'initial_email',
    created: getDayjsToday(),
    click_amount: 0,
    delivery_amount: numberOfRecipients,
    open_amount: 0,
    delivery_status: 'sent',
  }
  mutate(url, (data) => [newHistory, ...(data || [])], true)
}

function _addScheduledHistory(timestamp, surveyToken) {
  const url = `survey/survey/${surveyToken}/email-delivery-history/`
  const newHistory = {
    email_type: 'scheduled',
    created: timestamp,
    click_amount: null,
    delivery_amount: null,
    open_amount: null,
    delivery_status: 'sent',
  }
  mutate(url, (data) => [newHistory, ...(data || [])], true)
}

function _removeScheduledHistory(surveyToken) {
  const url = `survey/survey/${surveyToken}/email-delivery-history/`
  mutate(url, (data) => (Array.isArray(data) ? data.splice(0) : data), true)
}

function useNumberOfRecipients() {
  const surveyToken = useSurveyToken()
  const { data: audienceRules } = useAxios({
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
    skip: !surveyToken,
  })

  const customersMatchingAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/audience/`,
    data: {
      audience_rules: transformAudienceRulesDataToRequestData(audienceRules),
      show_list: false,
      all: false,
    },
    skip: !surveyToken || !audienceRules,
  })

  if (customersMatchingAudienceRulesApi.data) {
    return customersMatchingAudienceRulesApi.data.number_of_customer
  }
}

function transformAudienceRulesDataToRequestData(audienceRulesData) {
  const audienceRules = R.prop('audience_rules', audienceRulesData)
  if (audienceRules) {
    return audienceRules.map(AudienceRuleDataToRequestData)
  } else {
    return []
  }
}

function AudienceRuleDataToRequestData(audienceRuleData) {
  return {
    operator: audienceRuleData.operator,
    condition: audienceRuleData.audience_rule.condition,
    field: audienceRuleData.audience_rule.field,
    value: audienceRuleData.audience_rule.value,
  }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/OneoffEmailSendButton/container.js